import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import API from '@/lib/api'
import { cn } from '@/lib/utils'

import { editableZoneClassName } from './EditableZone'

export interface InvoicingPreviewFootnoteProps {
  footnote: string | null | undefined
  onMutate: () => void
}

export const InvoicingPreviewFootnote = ({
  footnote,
  onMutate,
}: InvoicingPreviewFootnoteProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings' })
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(footnote || '')

  const handleSave = () => {
    API.patch('/api/v2/settings', { footnote: value }).then(() => {
      toast.success(t('invoicing.generic.success'))
      setIsEditing(false)
      onMutate()
    })
  }

  if (isEditing) {
    return (
      <div className={cn(editableZoneClassName, 'space-y-2')}>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="min-h-16 text-[11px] text-foreground"
          placeholder={t('invoicing.footnote.label')}
        />
        <p className="text-[9px] text-muted-foreground italic">
          {t('invoicing.footnote.example')}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" className="h-7 text-xs" onClick={handleSave}>
            {t('invoicing.preview.footnote.save')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              setValue(footnote || '')
              setIsEditing(false)
            }}
          >
            {t('invoicing.preview.supplier.cancel')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        editableZoneClassName,
        'w-full text-left text-[11px] transition-colors hover:border-link',
        !footnote && 'text-muted-foreground italic',
      )}
      aria-label={t('invoicing.preview.footnote.edit')}
    >
      {footnote || t('invoicing.preview.footnote.placeholder')}
    </button>
  )
}
